import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DdpUserRegistrationResult {
  isAuthenticated: boolean;
  success: boolean;
  registered: boolean;
  alreadyRegistered: boolean;
  userId?: string;
  email?: string;
  message: string;
}

export interface UserConnectionInfo {
  userId: string;
  email?: string;
  connectionId: string;
  connectedAtUtc: string;
}

export interface UserRegistrationHubResult {
  registration: DdpUserRegistrationResult;
  connection: UserConnectionInfo;
  activeConnectionCount: number;
}

export interface ForceLogoutPayload {
  connectionId: string;
  email?: string;
  message: string;
}

export interface ForceLogoutResult {
  success: boolean;
  message: string;
  connectionId?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignalrUserRegistrationService {
  private readonly hubUrl = `${environment.apiUrl.replace(/\/+$/, '')}/hubs/user-registration`;
  private readonly registrationResultSubject = new BehaviorSubject<UserRegistrationHubResult | null>(null);

  private connection?: signalR.HubConnection;
  private startPromise?: Promise<void>;
  private forceLogoutInProgress = false;

  readonly registrationResult$ = this.registrationResultSubject.asObservable();

  constructor(private msalService: MsalService) { }

  async start(): Promise<void> {
    const account = this.getActiveAccount();
    if (!account) {
      return;
    }

    if (!this.connection) {
      this.connection = this.buildConnection();
    }

    if (this.connection.state === signalR.HubConnectionState.Connected ||
        this.connection.state === signalR.HubConnectionState.Connecting ||
        this.connection.state === signalR.HubConnectionState.Reconnecting) {
      return this.startPromise ?? Promise.resolve();
    }

    this.startPromise = this.connection
      .start()
      .then(() => {
        console.log('SignalR user registration connected');
      })
      .catch((error) => {
        console.warn('SignalR user registration connection failed:', error);
      })
      .finally(() => {
        this.startPromise = undefined;
      });

    return this.startPromise;
  }

  async stop(): Promise<void> {
    if (!this.connection) {
      return;
    }

    if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
      await this.connection.stop();
    }
  }

  async registerCurrentUser(): Promise<DdpUserRegistrationResult | null> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return null;
    }

    return this.connection.invoke<DdpUserRegistrationResult>('RegisterCurrentUser');
  }

  async getConnectedUsers(): Promise<UserConnectionInfo[]> {
    await this.start();

    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR connection is not connected.');
    }

    return this.connection.invoke<UserConnectionInfo[]>('GetConnectedUsers');
  }

  async logoutConnectedUser(connectionId: string, email?: string | null): Promise<ForceLogoutResult> {
    await this.start();

    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR connection is not connected.');
    }

    return this.connection.invoke<ForceLogoutResult>('LogoutConnectedUser', connectionId, email ?? null);
  }

  private buildConnection(): signalR.HubConnection {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => this.getAccessToken()
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connection.on('UserRegistered', (result: UserRegistrationHubResult) => {
      console.log('SignalR user registration completed:', result);
      this.registrationResultSubject.next(result);
    });

    connection.on('ForceLogout', (payload: ForceLogoutPayload) => {
      console.warn('SignalR force logout requested:', payload);
      this.handleForceLogout();
    });

    connection.onreconnected(() => {
      this.registerCurrentUser().catch(error =>
        console.warn('SignalR user re-registration failed after reconnect:', error)
      );
    });

    connection.onclose(error => {
      if (error) {
        console.warn('SignalR user registration connection closed:', error);
      }
    });

    return connection;
  }

  private handleForceLogout(): void {
    if (this.forceLogoutInProgress) {
      return;
    }

    this.forceLogoutInProgress = true;
    this.stop()
      .catch(error => console.warn('SignalR forced logout connection stop failed:', error))
      .finally(() => {
        this.msalService.logoutRedirect({
          postLogoutRedirectUri: window.location.origin
        });
      });
  }

  private getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0]
      ?? null;
  }

  private async getAccessToken(): Promise<string> {
    const account = this.getActiveAccount();
    if (!account) {
      return '';
    }

    const silentRequest: SilentRequest = {
      account,
      scopes: [this.getFullApiScope()]
    };

    try {
      const result: AuthenticationResult = await this.msalService.instance.acquireTokenSilent(silentRequest);
      if (result.account) {
        this.msalService.instance.setActiveAccount(result.account);
      }

      return result.accessToken;
    } catch (error) {
      console.warn('SignalR access token acquisition failed:', error);
      return '';
    }
  }

  private getFullApiScope(): string {
    const { tenantName, apiScope } = environment.msalConfig;
    return `https://${tenantName}.onmicrosoft.com/${apiScope}`;
  }
}
